// The text rules a page title and meta description obey: the budgets, the
// brand suffix, and how each is cut when it will not fit. Shared by the head
// renderer and the admin editors so both measure against the same numbers.
//
// A leaf on purpose: `server-seo.ts` has module-scope side effects (frozen
// tables, zod schemas) the bundler cannot tree-shake, so importing it from a
// React component would ship them to the browser.

export const SITE_NAME = 'Lean In Connect';

// Google truncates by rendered pixel width, not by character count: roughly
// 920px for a snippet and 580px for a title on desktop, both narrower on
// mobile. Character caps are therefore an approximation. 155 is the
// conservative end of the usual 155-160 range for a description. 60 is the
// top of the 50-60 range for a title because it is the last-resort cut, not
// the target: the editor counts against the point where the brand suffix
// stops fitting.
export const DESCRIPTION_MAX = 155;
export const TITLE_MAX = 60;

// A page title reads "Thing · Lean In Connect" so the brand is present
// without crowding out the distinctive part, which is what a searcher scans
// for.
export const TITLE_BRAND_SUFFIX = ` · ${SITE_NAME}`;

export function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

// A hard cut counts UTF-16 code units, so it can land between the halves of
// a surrogate pair and leave an orphaned high half at the end, which renders
// as a replacement character. Matching only D800-DBFF leaves an intact pair
// (whose last unit is a low half) untouched.
const DANGLING_HIGH_SURROGATE = /[\uD800-\uDBFF]$/;

// Cuts at the last word boundary inside `max`, appending an ellipsis. The
// 0.6 floor guards the degenerate case of a single very long token: rather
// than emit one character and an ellipsis, take the hard cut.
//
// `max` is the budget for the WHOLE result, ellipsis included, so the cut
// reserves that one character. Slicing to `max` and then appending would
// return max + 1 and quietly overrun the limit it is supposed to enforce.
//
// A budget of zero is answered before `room` is computed: a negative `room`
// would make `slice` count back from the end.
export function clampAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  if (max <= 0) return '';
  const room = max - 1;
  const cut = text.slice(0, room);
  const lastSpace = cut.lastIndexOf(' ');
  const body = lastSpace > room * 0.6 ? cut.slice(0, lastSpace) : cut;
  // Strip trailing punctuation before the dangling-surrogate check, because
  // stripping can expose a high surrogate that was not last before.
  return `${body.replace(/[,;:.\s]+$/, '').replace(DANGLING_HIGH_SURROGATE, '')}…`;
}

// For plain-text sources: the description columns, and the head's own copy.
export function clampDescription(text: string): string {
  return clampAtWord(collapseWhitespace(text), DESCRIPTION_MAX);
}

// The brand suffix is the first thing sacrificed when the whole title will
// not fit. A Circle name is what a searcher recognises and what they typed;
// the site name is the redundant half, and Google frequently appends the site
// name itself anyway. Truncating the name to preserve " · Lean In Connect"
// would throw away the useful half to keep the useless one. Only a subject
// that exceeds the cap on its own gets cut, and then at a word boundary.
export function composeTitle(subject: string | null): string {
  const trimmed = collapseWhitespace(subject ?? '');
  if (trimmed === '') return SITE_NAME;
  const withBrand = `${trimmed}${TITLE_BRAND_SUFFIX}`;
  if (withBrand.length <= TITLE_MAX) return withBrand;
  if (trimmed.length <= TITLE_MAX) return trimmed;
  return clampAtWord(trimmed, TITLE_MAX);
}

// The subject a resource or learning track page is titled by: the editor's
// search title when one is written, else the entity's own title. The head
// renderer and the SPA's tab title both read it, so a page's crawled title
// and its in-app title are one string. Blank counts as absent, as it does for
// every other editor-written search field.
export function editorialTitleSubject(entity: {
  readonly title: string;
  readonly seoTitle?: string | null;
}): string {
  return entity.seoTitle != null && entity.seoTitle.trim() !== '' ? entity.seoTitle : entity.title;
}
